import { useState } from 'react'

interface Integration {
  id: string
  name: string
  category: 'ats' | 'calendar' | 'communication' | 'hris'
  description: string
  logo: string
  status: 'connected' | 'available' | 'coming_soon'
  connectedAt?: string
  lastSync?: string
  syncStatus?: 'healthy' | 'warning' | 'error'
}

// Mock data - in production this comes from API
const MOCK_INTEGRATIONS: Integration[] = [
  // ATS
  {
    id: 'greenhouse',
    name: 'Greenhouse',
    category: 'ats',
    description: 'Sync candidates, jobs, and interview stages automatically',
    logo: '🌿',
    status: 'connected',
    connectedAt: 'Dec 15, 2025',
    lastSync: '2 minutes ago',
    syncStatus: 'healthy',
  },
  {
    id: 'lever',
    name: 'Lever',
    category: 'ats',
    description: 'Import candidates and push evaluation results',
    logo: '⚡',
    status: 'available',
  },
  {
    id: 'ashby',
    name: 'Ashby',
    category: 'ats',
    description: 'Bi-directional sync with evaluation data',
    logo: '🔷',
    status: 'available',
  },
  {
    id: 'workday-recruiting',
    name: 'Workday Recruiting',
    category: 'ats',
    description: 'Enterprise-grade ATS integration',
    logo: '🏢',
    status: 'coming_soon',
  },
  // Calendar
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    category: 'calendar',
    description: 'Schedule interviews and sync evaluator availability',
    logo: '📅',
    status: 'connected',
    connectedAt: 'Dec 10, 2025',
    lastSync: '5 minutes ago',
    syncStatus: 'healthy',
  },
  {
    id: 'outlook',
    name: 'Microsoft Outlook',
    category: 'calendar',
    description: 'Calendar and scheduling integration',
    logo: '📧',
    status: 'available',
  },
  // Communication
  {
    id: 'slack',
    name: 'Slack',
    category: 'communication',
    description: 'Get notified about evaluations, decisions, and actions',
    logo: '💬',
    status: 'connected',
    connectedAt: 'Dec 12, 2025',
    lastSync: '1 minute ago',
    syncStatus: 'healthy',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    category: 'communication',
    description: 'Notifications and collaboration',
    logo: '👥',
    status: 'coming_soon',
  },
  // HRIS
  {
    id: 'workday',
    name: 'Workday',
    category: 'hris',
    description: 'Sync employee data and org structure',
    logo: '🏢',
    status: 'available',
  },
  {
    id: 'bamboohr',
    name: 'BambooHR',
    category: 'hris',
    description: 'Employee records and onboarding',
    logo: '🎋',
    status: 'coming_soon',
  },
]

const CATEGORIES = [
  { id: 'ats', name: 'Applicant Tracking', description: 'Keep candidate data in sync' },
  { id: 'calendar', name: 'Calendar', description: 'Schedule and manage interviews' },
  { id: 'communication', name: 'Communication', description: 'Stay notified on hiring activity' },
  { id: 'hris', name: 'HR Systems', description: 'Sync with your people platform' },
]

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ArrowPathIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  )
}

function Cog6ToothIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const isConnected = integration.status === 'connected'
  const isComingSoon = integration.status === 'coming_soon'

  return (
    <div className={`border rounded-xl p-5 transition-all ${
      isConnected 
        ? 'border-emerald-200 bg-emerald-50/30' 
        : isComingSoon
          ? 'border-surface-100 bg-surface-50 opacity-60'
          : 'border-surface-200 bg-white hover:border-brand-200 hover:shadow-sm'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white border border-surface-200 flex items-center justify-center text-2xl shadow-sm">
            {integration.logo}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-surface-900">{integration.name}</h3>
              {isConnected && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                  <CheckCircleIcon className="w-3 h-3" />
                  Connected
                </span>
              )}
              {isComingSoon && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-surface-100 text-surface-500">
                  Coming soon
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-surface-500">{integration.description}</p>
            
            {isConnected && integration.lastSync && (
              <div className="mt-2 flex items-center gap-4 text-xs text-surface-500">
                <span className="flex items-center gap-1">
                  <ArrowPathIcon className="w-3 h-3" />
                  Last sync: {integration.lastSync}
                </span>
                {integration.syncStatus === 'healthy' && (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <CheckCircleIcon className="w-3 h-3" />
                    Healthy
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="shrink-0">
          {isConnected ? (
            <div className="flex items-center gap-2">
              <button className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors">
                <Cog6ToothIcon className="w-5 h-5" />
              </button>
              <button className="p-2 text-surface-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
          ) : isComingSoon ? (
            <button 
              disabled
              className="px-4 py-2 rounded-lg text-sm font-medium bg-surface-100 text-surface-400 cursor-not-allowed"
            >
              Notify me
            </button>
          ) : (
            <button className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm">
              Connect
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function IntegrationsSettings() {
  const [integrations] = useState(MOCK_INTEGRATIONS)

  const connectedCount = integrations.filter(i => i.status === 'connected').length

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-surface-900">Integrations</h2>
        <p className="mt-1 text-surface-600">
          Connect your existing tools to keep hiring data synchronized and teams informed.
        </p>
      </div>

      {/* Status summary */}
      <div className="p-6 sm:p-8 bg-surface-50">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="font-medium text-surface-900">
              {connectedCount} integration{connectedCount !== 1 ? 's' : ''} connected
            </p>
            <p className="text-sm text-surface-500">
              All syncs are running normally
            </p>
          </div>
        </div>
      </div>

      {/* Integrations by category */}
      {CATEGORIES.map((category) => {
        const categoryIntegrations = integrations.filter(i => i.category === category.id)
        if (categoryIntegrations.length === 0) return null

        return (
          <div key={category.id} className="p-6 sm:p-8">
            <div className="mb-4">
              <h3 className="font-medium text-surface-900">{category.name}</h3>
              <p className="text-sm text-surface-500">{category.description}</p>
            </div>
            <div className="space-y-3">
              {categoryIntegrations.map((integration) => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </div>
          </div>
        )
      })}

      {/* API access */}
      <div className="p-6 sm:p-8">
        <div className="border border-surface-200 rounded-xl p-5 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-surface-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-surface-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-surface-900">API Access</h3>
                <p className="mt-1 text-sm text-surface-500">
                  Build custom integrations with our REST API. Generate API keys and access documentation.
                </p>
              </div>
            </div>
            <button className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium border border-surface-300 text-surface-700 hover:bg-surface-50 transition-colors">
              Manage API keys
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

